import {
  FEED_DATASETS,
  type FeedDataset,
  SIGNAL_NOVELTY,
  SIGNAL_TRIAGE_STATES,
  type Signal,
  type SignalSeverity,
  type SignalTriageState,
  signalSeverityFor,
} from '@fruuit/shared'
import { Badge, Button, Callout, cn, StatusBadge } from '@fruuit/ui'
import { ExternalLink, FilePlus2, XCircle } from 'lucide-react'
import type { ReactNode } from 'react'
import { IdentifierTag } from '../canonical/IdentifierTag'
import { formatDate, formatDateTime } from '../feeds/format'

const BAND_LABEL: Record<SignalSeverity, string> = {
  low: 'Low',
  moderate: 'Moderate',
  serious: 'Serious',
  severe: 'Severe',
}

const BAND_CLASSES: Record<SignalSeverity, string> = {
  low: 'border-quiet-line bg-quiet-soft text-quiet',
  moderate: 'border-watch-line bg-watch-soft text-watch',
  serious: 'border-attention-line bg-attention-soft text-attention',
  severe: 'border-danger bg-danger text-white',
}

/** The generic 0–100 signal score with its severity band (never a provider metric). */
export function ScorePill({ score }: { score: number | null }) {
  if (score === null) return <span className="text-sage">—</span>
  const band = signalSeverityFor(score)
  return (
    <span
      title={`Signal score ${score} of 100 (${BAND_LABEL[band].toLowerCase()})`}
      className={cn(
        'inline-flex min-w-11 flex-col items-center rounded-md border px-1.5 py-0.5 leading-tight',
        BAND_CLASSES[band],
      )}
    >
      <span className="text-sm font-semibold tabular-nums">{score}</span>
      <span className="text-[10px] font-semibold uppercase tracking-wide">{BAND_LABEL[band]}</span>
    </span>
  )
}

export function TriageBadge({ state }: { state: SignalTriageState }) {
  return <StatusBadge status={state} label={SIGNAL_TRIAGE_STATES.labels[state]} />
}

export function NoveltyBadge({ novelty }: { novelty: Signal['novelty'] }) {
  if (!novelty) return null
  return (
    <Badge tone="outline" className="font-medium">
      {novelty === 'new' ? 'First report' : SIGNAL_NOVELTY.labels[novelty]}
    </Badge>
  )
}

export function canTriage(signal: Signal): boolean {
  return signal.triage_state === 'new' || signal.triage_state === 'mapping_review'
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-3 py-2 text-sm">
      <dt className="text-sage">{label}</dt>
      <dd className="min-w-0 text-ink">{children}</dd>
    </div>
  )
}

function datasetLabel(dataset: string): string {
  return FEED_DATASETS.labels[dataset as FeedDataset] ?? dataset
}

/** Signal detail: provenance, matched identifier, linked issuer and world event, triage. */
export function SignalDetail({
  signal,
  onDismiss,
  onPromote,
}: {
  signal: Signal
  onDismiss: () => void
  onPromote: () => void
}) {
  const open = canTriage(signal)
  return (
    <article aria-labelledby="signal-detail-title" className="flex flex-col gap-5">
      <header>
        <div className="flex flex-wrap items-center gap-2">
          <TriageBadge state={signal.triage_state} />
          <NoveltyBadge novelty={signal.novelty} />
          <Badge tone="outline">{signal.provider_name}</Badge>
        </div>
        <div className="mt-3 flex items-start gap-3">
          <ScorePill score={signal.signal_score} />
          <h2
            id="signal-detail-title"
            className="min-w-0 flex-1 text-lg font-semibold leading-snug text-ink"
          >
            {signal.title}
          </h2>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-ink-soft">{signal.summary}</p>
        {signal.topics.length > 0 ? (
          <p className="mt-3 flex flex-wrap gap-1.5">
            {signal.topics.map((topic) => (
              <Badge key={topic} tone="outline">
                {topic}
              </Badge>
            ))}
          </p>
        ) : null}
      </header>

      <section aria-label="Company match">
        <h3 className="text-xs font-semibold uppercase tracking-[var(--tracking-kicker)] text-teal-500">
          Company match
        </h3>
        <dl className="mt-1 divide-y divide-line-soft">
          <Row label="Delivered identifier">
            {signal.identifier ? (
              <IdentifierTag type={signal.identifier.type} value={signal.identifier.value} />
            ) : (
              'None delivered'
            )}
          </Row>
          <Row label="Issuer">
            {signal.issuer ? (
              <>
                <span className="font-semibold">{signal.issuer.legal_name}</span>
                <span className="block text-xs text-sage">
                  {signal.issuer.country} · {signal.issuer.sector}
                  {signal.mapped_by ? ` · matched by ${signal.mapped_by}` : ''}
                </span>
              </>
            ) : (
              <span className="font-semibold text-watch">Unmapped: held for mapping review</span>
            )}
          </Row>
          <Row label="Held by">
            {signal.held_by_org_count > 0
              ? `${signal.held_by_org_count} investor org${signal.held_by_org_count === 1 ? '' : 's'}`
              : 'No investor holds this issuer'}
          </Row>
        </dl>
        {signal.issuer ? null : (
          <p className="mt-2 text-xs leading-relaxed text-sage">
            Companies are matched by typed identifier only. A similar name is never treated as a
            match.
          </p>
        )}
      </section>

      {signal.world_event ? (
        <section aria-label="Linked world event">
          <h3 className="text-xs font-semibold uppercase tracking-[var(--tracking-kicker)] text-teal-500">
            Linked event
          </h3>
          <div className="mt-2 rounded-md border border-line bg-wash px-3 py-2.5 text-sm">
            <p className="font-semibold text-ink">{signal.world_event.title}</p>
            <p className="mt-0.5 text-xs text-sage">
              {formatDate(signal.world_event.occurs_on)} ·{' '}
              {signal.world_event.event_type.replace(/_/g, ' ')}
              {signal.world_event.severity ? ` · ${signal.world_event.severity}` : ''}
              {signal.world_event.storyline ? ` · storyline ${signal.world_event.storyline}` : ''}
            </p>
          </div>
        </section>
      ) : null}

      <section aria-label="Dates and provenance">
        <h3 className="text-xs font-semibold uppercase tracking-[var(--tracking-kicker)] text-teal-500">
          Dates and provenance
        </h3>
        <dl className="mt-1 divide-y divide-line-soft">
          <Row label="Event date">{formatDate(signal.event_date)}</Row>
          <Row label="Published">{formatDate(signal.publication_date)}</Row>
          <Row label="Delivered">{formatDateTime(signal.delivered_at, 'unverified')}</Row>
          <Row label="Retrieved">{formatDateTime(signal.retrieved_at)}</Row>
          <Row label="Dataset">{datasetLabel(signal.dataset)}</Row>
          <Row label="Provider record">
            <span className="break-all font-mono text-xs">{signal.provider_record_id}</span>
          </Row>
          {signal.source_url ? (
            <Row label="Source">
              <a
                href={signal.source_url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 break-all font-semibold text-teal underline"
              >
                Open source
                <ExternalLink aria-hidden="true" className="size-3.5 shrink-0" />
              </a>
            </Row>
          ) : null}
        </dl>
        <p className="mt-2 text-xs leading-relaxed text-sage">{signal.attribution}</p>
      </section>

      {signal.triage_state === 'dismissed' || signal.triage_state === 'promoted' ? (
        <Callout tone="note" title={SIGNAL_TRIAGE_STATES.labels[signal.triage_state]}>
          {signal.triaged_by_name ?? 'A team member'}, {formatDateTime(signal.triaged_at)}
          {signal.triage_note ? `: ${signal.triage_note}` : ''}
          {signal.promoted_record_id ? (
            <span className="block text-xs">Draft incident {signal.promoted_record_id}</span>
          ) : null}
        </Callout>
      ) : null}

      {open ? (
        <div className="flex flex-col gap-2 border-t border-line pt-4 sm:flex-row">
          <Button
            icon={<FilePlus2 />}
            onClick={onPromote}
            disabled={!signal.issuer}
            title={signal.issuer ? undefined : 'Map the signal to an issuer first'}
          >
            Promote to draft incident
          </Button>
          <Button variant="secondary" icon={<XCircle />} onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      ) : null}
    </article>
  )
}
